import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const LearnMore = () => {
  const { isDarkMode } = useTheme();

  return (
    <div className="dashboard-page" style={{ 
      background: isDarkMode ? 'linear-gradient(135deg, #282440 0%, #1a1a2e 100%)' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      color: isDarkMode ? 'white' : '#333',
      paddingTop: '0'
    }}>
      <div className="container">
        {/* Header Section with Logo and Title */}
        <div style={{ marginBottom: '3rem' }}>
          {/* Centered Title */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <h1 style={{ 
              fontSize: '3rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              color: 'white'
            }}>
              Learn More
            </h1>
          </div>
          
          {/* Subheader text - centered on the page */}
          <div style={{ 
            marginBottom: '1.5rem'
          }}>
            <p style={{ 
              fontSize: '1.2rem', 
              color: isDarkMode ? '#B0B0B0' : '#666',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6',
              textAlign: 'center'
            }}>
              Discover the power of AI integration and MCP Servers in bioinformatics. Learn about the history, advantages, and future of computational biology tools.
            </p>
          </div>
          
          {/* Learn More badge */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(0, 123, 255, 0.1)',
              borderRadius: '20px',
              border: `1px solid ${isDarkMode ? 'rgba(139, 92, 246, 0.3)' : 'rgba(0, 123, 255, 0.3)'}`
            }}>
              <span style={{ 
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                Educational Resource Center
              </span>
            </div>
          </div>
        </div>

        {/* What is MCP Section */}
          <div style={{ marginBottom: '4rem', textAlign: 'left' }}>
            <h2 style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              marginBottom: '2rem',
              color: isDarkMode ? '#8B5CF6' : '#007bff',
              borderBottom: `3px solid ${isDarkMode ? '#8B5CF6' : '#007bff'}`,
              paddingBottom: '0.5rem',
              textAlign: 'center'
            }}>
              What is MCP and MCP Servers
            </h2>
            <div style={{
              backgroundColor: isDarkMode ? 'rgba(0, 153, 255, 0.05)' : 'rgba(0, 153, 255, 0.05)',
              padding: '2rem',
              borderRadius: '12px',
              border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
              marginBottom: '2rem',
            }}>
              <p style={{
                fontSize: '1.2rem',
                lineHeight: '1.7',
                marginBottom: '1.5rem',
                color: isDarkMode ? '#D1D5DB' : '#666'
              }}>
                The Model Context Protocol (MCP) is an <strong>open standard</strong> introduced by Anthropic in <strong>November 2024</strong>, designed to standardize how AI models (especially large language models / agents) connect to external data sources, tools, and services.
              </p>
              <p style={{
                fontSize: '1.2rem',
                lineHeight: '1.7',
                color: isDarkMode ? '#D1D5DB' : '#666'
              }}>
                An <strong>MCP server</strong> implements this protocol: it is a component that exposes data, tools, services, or custom logic via MCP, so that MCP clients (AI agents, tools, apps) can discover, query, and act through them. In short: MCP servers make data and functions accessible under a uniform API / interface, simplifying integration.
              </p>
            </div>
          </div>

          {/* History & Motivation Section */}
          <div style={{ marginBottom: '4rem', textAlign: 'left' }}>
            <h2 style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              marginBottom: '2rem',
              color: isDarkMode ? '#8B5CF6' : '#007bff',
              borderBottom: `3px solid ${isDarkMode ? '#8B5CF6' : '#007bff'}`,
              paddingBottom: '0.5rem',
              textAlign: 'center'
            }}>
              History & Motivation
            </h2>
            <div style={{
              backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
              padding: '2rem',
              borderRadius: '12px',
              border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
            }}>
              <ul style={{ paddingLeft: '1.5rem', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                <li style={{ marginBottom: '1rem', fontSize: '1.1rem', lineHeight: '1.6' }}>
                  Before MCP, integrating AI / LLMs with external data or tools often required building custom connectors for each data source (APIs, bespoke interfaces, etc.). This is burdensome for maintenance, scaling, and consistency.
                </li>
                <li style={{ marginBottom: '1rem', fontSize: '1.1rem', lineHeight: '1.6' }}>
                  MCP was officially announced by Anthropic on <strong>25 Nov 2024</strong>, with open‐source specification, SDKs, and initial reference server implementations for common tools and services (e.g. GitHub, Google Drive, Slack, Postgres, Puppeteer) to help bootstrap adoption.
                </li>
                <li style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                  Since then, there has been growing interest and early adoption: both in enterprise settings, in developer tools, and in academic experiments (especially bioinformatics).
                </li>
              </ul>
            </div>
          </div>

          {/* Advantages Section */}
          <div style={{ marginBottom: '4rem', textAlign: 'left' }}>
            <h2 style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              marginBottom: '2rem',
              color: isDarkMode ? '#8B5CF6' : '#007bff',
              borderBottom: `3px solid ${isDarkMode ? '#8B5CF6' : '#007bff'}`,
              paddingBottom: '0.5rem',
              textAlign: 'center'
            }}>
              Advantages of Using MCP Servers
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 10px 25px rgba(139, 92, 246, 0.2)' : '0 10px 25px rgba(0, 123, 255, 0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  Standardization & Interoperability
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  MCP provides a common protocol so different tools / data sources can be accessed in a uniform way. Reduces duplicated work: instead of writing many custom connectors, developers can build or reuse MCP servers or SDKs.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 10px 25px rgba(139, 92, 246, 0.2)' : '0 10px 25px rgba(0, 123, 255, 0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  Context Preservation & Memory Management
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  MCP servers help maintain context (dialogue history, metadata, state) more reliably across interactions. This helps in avoiding loss of context, managing memory, pruning irrelevant history, summarization.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 10px 25px rgba(139, 92, 246, 0.2)' : '0 10px 25px rgba(0, 123, 255, 0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  Scalability & Reproducibility
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  MCP enables services (webservers, databases) to become "MCP‐enabled", making workflows more reproducible and automatable. For large systems with many data sources, many models, etc., MCP simplifies scaling.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 10px 25px rgba(139, 92, 246, 0.2)' : '0 10px 25px rgba(0, 123, 255, 0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  Tool / Agent Integration & Automation
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  Allows agents / LLMs to access data, execute actions, chain tools, retrieve external data, etc., more seamlessly. Supports "agentic workflows", where LLMs don't just reply to queries, but perform tasks over time, across tools.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 10px 25px rgba(139, 92, 246, 0.2)' : '0 10px 25px rgba(0, 123, 255, 0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  Governance, Traceability & Security
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  MCP servers can include versioning, audit logs, metadata about what tools were used, what data sources accessed. This helps with debugging, compliance, and accountability.
                </p>
              </div>
            </div>
          </div>

          {/* Bioinformatics Application Section */}
          <div style={{ marginBottom: '4rem', textAlign: 'left' }}>
            <h2 style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              marginBottom: '2rem',
              color: isDarkMode ? '#8B5CF6' : '#007bff',
              borderBottom: `3px solid ${isDarkMode ? '#8B5CF6' : '#007bff'}`,
              paddingBottom: '0.5rem',
              textAlign: 'center'
            }}>
              Special Application to Bioinformatics
            </h2>
            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.6',
              marginBottom: '2rem',
              color: isDarkMode ? '#D1D5DB' : '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              Bioinformatics has specific characteristics (large datasets, reproducibility requirements, complex pipelines), which make MCP (and MCP servers) especially promising.
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
              <div>
                <h3 style={{ 
                  fontSize: '1.6rem', 
                  fontWeight: 'bold', 
                  marginBottom: '1.5rem', 
                  color: isDarkMode ? '#10B981' : '#059669',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.8rem' }}>✅</span>
                  Why bioinformatics is a good fit
                </h3>
                <div style={{
                  backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                }}>
                  <ul style={{ paddingLeft: '1.5rem', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                    <li style={{ marginBottom: '1.2rem', lineHeight: '1.6' }}>
                      <strong style={{ color: isDarkMode ? '#10B981' : '#059669' }}>Rich, heterogeneous data sources:</strong> Genomes, expression data, imaging, literature, proprietary / public databases. MCP allows integrating many of these in a standardized way.
                    </li>
                    <li style={{ marginBottom: '1.2rem', lineHeight: '1.6' }}>
                      <strong style={{ color: isDarkMode ? '#10B981' : '#059669' }}>Need for reproducibility & provenance:</strong> Scientific workflows must record exactly which data / versions / parameters were used; MCP servers can help with metadata, versioning, logging.
                    </li>
                    <li style={{ marginBottom: '1.2rem', lineHeight: '1.6' }}>
                      <strong style={{ color: isDarkMode ? '#10B981' : '#059669' }}>Interactive / exploratory workflows:</strong> Researchers often need to try queries, refine, link datasets, pull literature, re‐compute analyses. Agents using MCP can support this fluid exploration.
                    </li>
                    <li style={{ lineHeight: '1.6' }}>
                      <strong style={{ color: isDarkMode ? '#10B981' : '#059669' }}>Large computational pipelines:</strong> Automating bioinformatics pipelines (alignment, variant calling, etc.) can be partially orchestrated via MCP if servers expose tools and logic appropriately.
                    </li>
                  </ul>
                </div>
              </div>
              
              <div>
                <h3 style={{ 
                  fontSize: '1.6rem', 
                  fontWeight: 'bold', 
                  marginBottom: '1.5rem', 
                  color: isDarkMode ? '#EF4444' : '#DC2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.8rem' }}>⚠️</span>
                  Challenges & Constraints
                </h3>
                <div style={{
                  backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                }}>
                  <ul style={{ paddingLeft: '1.5rem', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                    <li style={{ marginBottom: '1.2rem', lineHeight: '1.6' }}>
                      <strong style={{ color: isDarkMode ? '#EF4444' : '#DC2626' }}>Security & Trust:</strong> Bioinformatics data may be sensitive (e.g. patient data), so any MCP server must have strong access control, auditing, and isolation.
                    </li>
                    <li style={{ marginBottom: '1.2rem', lineHeight: '1.6' }}>
                      <strong style={{ color: isDarkMode ? '#EF4444' : '#DC2626' }}>Maintainability:</strong> Bioinformatics tools are often complex; wrapping or exposing them via MCP adds overhead. Legacy servers might require rewriting or adapting interfaces.
                    </li>
                    <li style={{ marginBottom: '1.2rem', lineHeight: '1.6' }}>
                      <strong style={{ color: isDarkMode ? '#EF4444' : '#DC2626' }}>Performance / Cost:</strong> Large data transfers, heavy compute, real‐time responses may be expensive. MCP server architectures must be designed to handle batch / streaming / caching etc.
                    </li>
                    <li style={{ lineHeight: '1.6' }}>
                      <strong style={{ color: isDarkMode ? '#EF4444' : '#DC2626' }}>Standardization of metadata & schemas:</strong> For agents to reliably use multiple bioinformatics MCP servers, metadata formats must be consistent.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Future Perspectives Section */}
          <div style={{ marginBottom: '4rem', textAlign: 'left' }}>
            <h2 style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              marginBottom: '2rem',
              color: isDarkMode ? '#8B5CF6' : '#007bff',
              borderBottom: `3px solid ${isDarkMode ? '#8B5CF6' : '#007bff'}`,
              paddingBottom: '0.5rem',
              textAlign: 'center'
            }}>
              Future Perspectives
            </h2>
            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.6',
              marginBottom: '2rem',
              color: isDarkMode ? '#D1D5DB' : '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              What's next? What could MCP servers enable or evolve toward, especially in bioinformatics and scientific research more broadly.
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 8px 20px rgba(139, 92, 246, 0.15)' : '0 8px 20px rgba(0, 123, 255, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  Broader Adoption in Bioinformatics
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  More bioinformatics databases, tools, portals will become MCP-compatible. Legacy web servers will adopt "breadcrumbs" or wrapper layers to gradually become MCP‐ready.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 8px 20px rgba(139, 92, 246, 0.15)' : '0 8px 20px rgba(0, 123, 255, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  MCP App Stores / Ecosystem
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  Curated repositories of MCP server modules/packages (for querying gene expression, variant databases, literature mining, etc.), which are tested for security, compatibility, versioned.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 8px 20px rgba(139, 92, 246, 0.15)' : '0 8px 20px rgba(0, 123, 255, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  Agentic Discovery Pipelines
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  As more tools become MCP‐enabled, it's easier to build agents that can plan entire research workflows: decide which datasets to pull, what analyses to run, interpret results, generate visualizations.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 8px 20px rgba(139, 92, 246, 0.15)' : '0 8px 20px rgba(0, 123, 255, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  Enhanced Security & Compliance
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  More work on MCP‐specific security standards, automated auditing tools, and frameworks to certify MCP servers for scientific and sensitive data applications.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 8px 20px rgba(139, 92, 246, 0.15)' : '0 8px 20px rgba(0, 123, 255, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  Performance & Scalability Enhancements
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  More efficient transport protocols, caching, summarization, incremental retrieval of large datasets, load balancing, distributed MCP servers / federated setups.
                </p>
              </div>
              
              <div style={{
                backgroundColor: isDarkMode ? '#212129' : '#f8f9fa',
                padding: '2rem',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = isDarkMode ? '0 8px 20px rgba(139, 92, 246, 0.15)' : '0 8px 20px rgba(0, 123, 255, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', marginBottom: '1rem', color: isDarkMode ? '#8B5CF6' : '#007bff' }}>
                  User Interfaces for Non‐Experts
                </h3>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: isDarkMode ? '#D1D5DB' : '#666' }}>
                  Making MCP servers and clients easier to use by non‐tech specialists (wet lab scientists, clinicians) via GUIs, notebooks, dashboards, etc.
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div style={{
            backgroundColor: '#282828',
            color: 'white',
            padding: '3rem 2rem',
            borderRadius: '16px',
            textAlign: 'center',
            marginTop: '3rem'
          }}>
            <h3 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Ready to Transform Your Research?
            </h3>
            <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: '0.9' }}>
              Join the next generation of scientific computing. Experience the power of AI-driven bioinformatics today.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link 
                to="/mcp-servers" 
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '1.1rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                View MCP Servers
              </Link>
              <Link 
                to="/workflows" 
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '1.1rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                View Workflows
              </Link>
            </div>
          </div>
      </div>
    </div>
  );
};

export default LearnMore;

